// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

import * as React from "react";
import { connect } from "react-redux";
import { WrappedComponentProps, defineMessages, injectIntl, FormattedMessage } from "react-intl";
import { Modal, Button } from "react-bootstrap";
import { Location, NavigateFunction } from "react-router-dom";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { isSet } from "utils";
import { ActionType, IAction, raiseAction } from "actions";
import { IState } from "states/states";
import { IStateGuiConfirmModal } from "states/guistates";

type IConfirmModalProps = IStateGuiConfirmModal;

const mapStateToProps = (state: IState) => {
  return {
    ...state.gui.confirmModal,
  };
};

interface IConfirmModalDispatch {
  onDoAction: (action: ActionType, payload: any) => void;
  onChangeLocation: (location: Location, navigate: NavigateFunction) => void;
  onCancelClick: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IConfirmModalDispatch = {
    onDoAction: (action: ActionType, payload: any) => {
      dispatch(raiseAction(ActionType.ACT_CONFIRM_CANCEL));
      dispatch(raiseAction(action, payload));
    },
    onChangeLocation: (location: Location, navigate: NavigateFunction) => {
      // change location, which will also clear dirty flag and close the modal
      navigate(location, { state: { resetUnsavedChanges: true } });
    },
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_CONFIRM_CANCEL));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  unsavedChangesTitle: {
    id: "Unsaved Changes",
  },
  unsavedChangesMessage: {
    id: "There are unsaved changes! On OK you confirm reverting the changes. Press Cancel if you want to save your changes.",
  },
});

class ConfirmModalComponent extends React.Component<
  IConfirmModalProps & IConfirmModalDispatch & WrappedComponentProps & RoutedProps
> {
  public render() {
    return (
      <Modal show={this.props.isOpen} onHide={this.props.onCancelClick}>
        <Modal.Header closeButton>
          {isSet(this.props.title)
            ? this.props.title
            : this.props.intl.formatMessage(messages.unsavedChangesTitle)}
        </Modal.Header>
        <Modal.Body>
          {isSet(this.props.title)
            ? this.props.message ?? ""
            : this.props.intl.formatMessage(messages.unsavedChangesMessage)}
        </Modal.Body>
        <Modal.Footer>
          <Button
            id="confirmCancel"
            variant="secondary"
            className="me-1"
            onClick={this.props.onCancelClick}
          >
            {!isSet(this.props.action) && !isSet(this.props.location) ? (
              <FormattedMessage id="OK" />
            ) : (
              <FormattedMessage id="Cancel" />
            )}
          </Button>
          {(isSet(this.props.action) || isSet(this.props.location)) && (
            <Button
              id="confirmOk"
              variant="primary"
              onClick={
                this.props.action
                  ? (ev) => this.props.onDoAction(this.props.action!, this.props.actionPayload)
                  : (ev) => this.props.onChangeLocation(this.props.location!, this.props.navigate)
              }
            >
              <FormattedMessage id="OK" />
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(ConfirmModalComponent)),
);
